import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  IconButton,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Footprints, Trash2, Users } from 'lucide-react';
import { format, startOfWeek, endOfWeek, isWithinInterval, parseISO } from 'date-fns';
import { DatabaseSchema, WalkEntry } from '../../types';

interface Props {
  database: DatabaseSchema | null;
  onAddWalk: (walk: WalkEntry) => Promise<void>;
  onDeleteWalk: (id: string) => Promise<void>;
}

const PRESETS = [15, 30, 45, 60];
const EFFORT_LABELS = ['', 'Gentle', 'Moderate', 'Brisk', 'Hard', 'Very hard'];

export const WalkLogger: React.FC<Props> = ({ database, onAddWalk, onDeleteWalk }) => {
  const [duration, setDuration] = useState<number>(30);
  const [withPartner, setWithPartner] = useState(true);
  const [distance, setDistance] = useState<string>('');
  const [effort, setEffort] = useState<number>(2);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const walks = useMemo(() => database?.walks ?? [], [database?.walks]);
  const target = database?.preferences?.weeklyActivityMinutesTarget ?? 150;
  const mode = database?.preferences?.targetMode ?? 'weekly_minutes';

  const thisWeekMinutes = useMemo(() => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    return walks
      .filter((w) => {
        try {
          return isWithinInterval(parseISO(w.date), { start, end });
        } catch {
          return false;
        }
      })
      .reduce((sum, w) => sum + (w.durationMin || 0), 0);
  }, [walks]);

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const walkedToday = walks.some((w) => w.date === todayStr);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onAddWalk({
        id: `walk-${Date.now()}`,
        date: todayStr,
        durationMin: duration,
        withPartner,
        distanceKm: distance ? Number(distance) : undefined,
        perceivedEffort: effort as 1 | 2 | 3 | 4 | 5,
        notes: notes || undefined,
      });
      setNotes('');
      setDistance('');
    } finally {
      setSaving(false);
    }
  };

  const recent = [...walks].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 7);

  const targetRatio = mode === 'weekly_minutes' ? Math.min(thisWeekMinutes / target, 1) : undefined;

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Footprints size={22} />
            <Typography variant="h6" fontWeight={700}>Walks</Typography>
          </Stack>

          {mode === 'weekly_minutes' ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                This week: <strong>{thisWeekMinutes} min</strong> · target {target} min
              </Typography>
              <Box sx={{
                mt: 0.5,
                height: 8,
                borderRadius: 4,
                backgroundColor: 'action.hover',
                overflow: 'hidden',
              }}>
                <Box sx={{
                  width: `${(targetRatio ?? 0) * 100}%`,
                  height: '100%',
                  backgroundColor: 'primary.main',
                  transition: 'width 300ms',
                }} />
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {walkedToday ? '✓ walked today' : "Haven't walked yet today"}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">Duration</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {PRESETS.map((m) => (
              <Chip
                key={m}
                label={`${m} min`}
                color={duration === m ? 'primary' : 'default'}
                onClick={() => setDuration(m)}
                sx={{ minHeight: 36 }}
              />
            ))}
          </Stack>

          <Box sx={{ px: 1, mb: 2 }}>
            <Slider
              value={duration}
              onChange={(_, v) => setDuration(v as number)}
              min={5}
              max={120}
              step={5}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}m`}
            />
          </Box>

          <FormControlLabel
            control={<Switch checked={withPartner} onChange={(e) => setWithPartner(e.target.checked)} />}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Users size={16} />
                <span>Walking with partner</span>
              </Stack>
            }
            sx={{ mb: 1 }}
          />

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              label="Distance (km, optional)"
              type="number"
              size="small"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              fullWidth
              inputProps={{ step: 0.1, min: 0 }}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">Effort: {EFFORT_LABELS[effort]}</Typography>
          <Slider
            value={effort}
            onChange={(_, v) => setEffort(v as number)}
            min={1}
            max={5}
            step={1}
            marks
            sx={{ mb: 2 }}
          />

          <TextField
            label="Notes (optional)"
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSave}
            disabled={saving}
            startIcon={<Footprints size={18} />}
          >
            Log {duration}-min walk
          </Button>
        </CardContent>
      </Card>

      {recent.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Recent walks</Typography>
            {recent.map((w) => (
              <Box
                key={w.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {format(parseISO(w.date), 'EEE d MMM')} · {w.durationMin} min
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {w.withPartner ? 'With partner' : 'Solo'}
                    {w.distanceKm ? ` · ${w.distanceKm} km` : ''}
                    {w.perceivedEffort ? ` · ${EFFORT_LABELS[w.perceivedEffort]}` : ''}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => onDeleteWalk(w.id)} sx={{ color: 'text.secondary' }}>
                  <Trash2 size={16} />
                </IconButton>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
